import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Card, CardBody, CardTitle, Table, Spinner, Button } from "reactstrap";
import Chart from "react-apexcharts";
import { MessageCircle, Calendar, Calendar as CalendarMonth, Clock, AlertTriangle, CloudOff, Shield } from "react-feather";
import { getDashboardData } from "../../API/Backend";
import "flatpickr/dist/themes/material_green.css";
import Flatpickr from "react-flatpickr";
import Skeleton from "react-loading-skeleton";
import { useNavigate } from "react-router-dom";
import { useClient } from "../../Utilities/ClientContext";

export default function Dashboard() {
  const navigate = useNavigate();
  const { activeClient } = useClient();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState([]);
  const [platform, setPlatform] = useState({ series: [], labels: [] });
  const [sentiment, setSentiment] = useState({ series: [], labels: [] });
  const [error, setError] = useState(null);
  const [date, setDate] = useState([]);
  const datePickerRef = useRef("");

  useEffect(() => {
    if (!activeClient) return;
    setLoading(true);
    const s = date[0] ?? null
    const e = date[1] ?? null
    fetchDashboardData(s,e);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, activeClient]);

  const fetchDashboardData = (start = null, end = null) => {
    setLoading(true);
    setError(null);
    getDashboardData({ clientName: activeClient, ...(start && { startDate: start }), ...(end && { endDate: end }) }).then((res) => {
      const data = res.data.data;
      setData(data)
      setStats([
        {
          title: "Chats Today",
          value: data.chatsToday,
          icon: <MessageCircle size={48} color="#4e73df" />,
          bg: "#e7f1ff"
        },
        {
          title: "Chats Last 7 Days",
          value: data.chatsLast7Days,
          icon: <Calendar size={48} color="#1cc88a" />,
          bg: "#e8f9f1"
        },
        {
          title: "Chats This Month",
          value: data.chatsThisMonth,
          icon: <CalendarMonth size={48} color="#36b9cc" />,
          bg: "#e0f7fa"
        },
        {
          title: "Total Chats",
          value: data.totalChats,
          icon: <CalendarMonth size={48} color="#36b9cc" />,
          bg: "#e0f7fa"
        },
        {
          title: "Avg Chat Duration (min)",
          value: (data.averageChatDurationMs / 60000).toFixed(2),
          icon: <Clock size={48} color="#f6c23e" />,
          bg: "#fff8e5"
        },
        {
          title: "Issues Resolved ( by AI )",
          value: data.resolvedCount,
          icon: <Shield size={48} color="#1cc88a" />,
          bg: "#e8f9f1"
        },
        {
          title: "Chats Closed (inactivity)",
          value: data.closedDueToInactivity,
          icon: <Clock size={48} color="#f6c23e" />,
          bg: "#fff8e5"
        },
      ])
      setPlatform({ series: Object.values(data.platformUsers), labels: Object.keys(data.platformUsers) })
      setSentiment({ series: Object.values(data.sentimentStats).map((s) => s.count), labels: Object.keys(data.sentimentStats) })
    }).catch((err) => {
      console.error(err);
      setData(null);
      // e.g. 503 when the database is busy; message comes from the API
      setError(err?.response?.data?.message || "Unable to load the dashboard.");
    }).finally(() => setLoading(false));
  }

  const filterByDate = (dates) => {
    if(dates.length){
      const s = new Date(dates[0]);
      // Single-day pick has no second date; include the whole end day
      const e = new Date(dates[1] ?? dates[0]);
      e.setHours(23, 59, 59, 999);
      const start = s.toISOString();
      const end = e.toISOString();
      setDate([start,end]);
    }
    else{
      setDate([]);
    }
  }

  return (
    <Container fluid>
      <Row>
        <Col>
          <h2>Dashboard Overview</h2>
        </Col>
        <Col className="text-end">
          <div style={{width: "250px", marginLeft: "auto"}}>
            <Flatpickr disabled={loading} ref={datePickerRef} id="selectedDate" className="form-control d-block bg-white" placeholder="Filter by Date" options={{ mode: "range", dateFormat: "d-m-Y", maxDate: new Date() }} onClose={filterByDate}/>
          </div>
        </Col>
      </Row>
      {!loading && (error || !data) ? (
        <Card className="p-4 mt-3 text-center">
          <p className="mb-3">{error || "Unable to load the dashboard."}</p>
          <div>
            <Button color="primary" onClick={() => fetchDashboardData(date[0] ?? null, date[1] ?? null)}>Retry</Button>
          </div>
        </Card>
      ) : (
      <>
      <Row className="mt-3">
        {
          loading ?
          [...Array(7)].map((a,i)=>(
            <Col sm="6" md="4" className="mb-3" key={i}>
              <Skeleton height={92} className="w-100"/>
            </Col>
          )) :
          stats.map((s, i) => (
            <Col sm="6" md="4" className="mb-3" key={i}>
              <Card className="d-flex flex-row align-items-center p-3" style={{ backgroundColor: s.bg }}>
                <div className="flex-grow-1">
                  <CardTitle tag="h6">{s.title}</CardTitle>
                  <h3 className="mb-0">{s.value}</h3>
                </div>
                <div>{s.icon}</div>
              </Card>
            </Col>
          ))
        }
      </Row>
      <Row className="mt-4">
        <Col md="6" className="mb-4">
          {
            loading ?
              <Skeleton className="w-100" height={470}/>:
              <Card className="p-3">
                  <Chart options={{ chart: { type: "pie", fontFamily: "inherit" }, labels: platform.labels, title: { text: "Platform Users", align: "center" }, legend: false}} series={platform.series} type="pie" height={300} />
                  {
                    [...Array(3)].map((a,i)=>(
                      <li key={i} className={`py-2 d-flex align-items-center justify-content-between px-3 text-${i === 0 ? "info" : i === 1 ? "success" : "warning"}`}>
                          <span className='d-flex align-items-center'>
                              <i className={`ri-${ i === 0 ? "apple" : i === 1 ? "android" : "window" }-line`} style={{fontSize: "20px", fontWeight: "500"}}></i>
                              <p className="text-muted mb-0 ms-1 text-truncate text-uppercase" style={{fontSize: "14px", fontWeight: "500"}}>{platform.labels[i]}</p>
                          </span>
                          <h5 className="font-size-16 m-0 text-right">{ platform.series[i] }</h5>
                      </li>
                    )) 
                  }
              </Card>
          }
        </Col>
        <Col md="6" className="mb-4">
          {
            loading ?
            <Skeleton className="w-100" height={470}/>:
            <Card className="p-3">
                <Chart options={{ chart: { type: "pie", fontFamily: "inherit" }, labels: sentiment.labels, title: { text: "Sentiment Stats", align: "center" }, legend: false}} series={sentiment.series} type="pie" height={300}/>
                {
                  [...Array(3)].map((a,i)=>(
                    <li key={i} className={`py-2 d-flex align-items-center cursor-pointer justify-content-between px-3 text-${i === 0 ? "success" : i === 1 ? "danger" : "warning"}`} onClick={()=>{ navigate(`/chatlogs?sentiment=${i === 0 ? "happy" : i === 1 ? "sad" : "neutral"}`) }}>
                        <span className='d-flex align-items-center'>
                            <i className={`ri-emotion-${ i === 0 ? "happy" : i === 1 ? "unhappy" : "normal" }-line`} style={{fontSize: "20px", fontWeight: "500"}}></i>
                            <p className="text-muted mb-0 ms-1 text-truncate" style={{fontSize: "14px", fontWeight: "500"}}>{ i === 0 ? "Happy" : i === 1 ? "Sad" : "Neutral" }</p>
                        </span>
                        <h5 className="font-size-16 m-0 text-right">{ sentiment.series[i] }</h5>
                    </li>
                  )) 
                }
            </Card>
          }
        </Col>
      </Row>
      <Row className="mt-4">
        <Col md="6" className="mb-4">
          {
            loading ?
            <Skeleton className="w-100" height={330}/>:
            <Card className="p-3">
                <Chart
                  options={{ chart: { type: "donut", toolbar: { show: false } }, labels: ["Forwarded", "Not Forwarded"], title: { text: "Chats Forwarded to Human", align: "center" }, legend: { position: "bottom" } }}
                  series={[ data.chatsForwardedToHuman.count, data.totalChats - data.chatsForwardedToHuman.count ]}
                  type="donut"
                  height={300}
                />
            </Card>
          }
        </Col>
        <Col md="6" className="mb-4">
          {
            loading ?
            <Skeleton className="w-100" height={330}/>:
            <Card className="p-3">
                <Chart
                  options={{ chart: { type: "donut", toolbar: { show: false } }, labels: ["Order Inquiry", "Other Chats"], title: { text: "Order Inquiry Rate", align: "center" }, legend: { position: "bottom" } }}
                  series={[ data.orderInquiry.count, data.totalChats - data.orderInquiry.count ]}
                  type="donut"
                  height={300}
                />
            </Card>
          }
        </Col>
      </Row>

      <Row>
          <Col>
            <CardBody>
              <Card className="p-3">
                  <h5>Top FAQs</h5>
                  <Table striped={!loading} bordered responsive>
                    <thead>
                      <tr>
                        <th className="text-left px-3">FAQ</th>
                        <th className="text-center px-3">Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {
                        loading ?
                        [...Array(5)].map((a,i)=>
                          <tr key={i}>
                              <td className="px-3"><Skeleton height={16} width={400}/></td>
                              <td className="text-center px-3"><Skeleton height={16} width={20}/></td>
                            </tr>
                        ) :
                        data.topFAQs.length > 0 ? (
                          data.topFAQs.map((faq, i) => (
                            <tr key={i}>
                              <td className="px-3">{faq._id}</td>
                              <td className="text-center px-3">{faq.count}</td>
                            </tr>
                          ))
                        ) : <tr><td colSpan="2" className="text-center">No top FAQs available</td></tr>
                      }
                    </tbody>
                  </Table>
              </Card>
            </CardBody>
          </Col>
      </Row>
      </>
      )}
    </Container>
  );
}
